import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  ArrowLeft,
  Edit,
  Truck,
  Package,
  MapPin,
  Calendar,
  DollarSign,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { ShipmentStatusBadge } from "./ShipmentsPage";

interface ShipmentDetail {
  id: string;
  shipmentNumber: string;
  direction: string;
  mode: string | null;
  status: string;
  trackingNumber: string | null;
  proNumber: string | null;
  billOfLading: string | null;
  containerNumber: string | null;
  bookingNumber: string | null;
  carrier: string | null;
  carrierName?: string | null;
  scheduledPickup: string | null;
  actualPickup: string | null;
  scheduledArrival: string | null;
  actualArrival: string | null;
  shipDate: string | null;
  estimatedArrival: string | null;
  shippingCost: string | null;
  totalCost: string | null;
  weight: string | null;
  weightUom: string | null;
  pieces: number | null;
  palletCount: number | null;
  podSignedBy: string | null;
  podSignedAt: string | null;
  podNotes: string | null;
  notes: string | null;
  purchaseOrder?: { id: string; poNumber: string };
  order?: { id: string; orderNumber: string; account?: { name: string } };
  vendor?: { id: string; name: string };
  originWarehouse?: { id: string; name: string; code: string };
  destinationWarehouse?: { id: string; name: string; code: string };
}

const editSchema = z.object({
  status: z.string(),
  trackingNumber: z.string().optional(),
  proNumber: z.string().optional(),
  containerNumber: z.string().optional(),
  billOfLading: z.string().optional(),
  notes: z.string().optional(),
});

type EditFormData = z.infer<typeof editSchema>;

const STATUS_OPTIONS = [
  "PLANNED", "BOOKED", "PICKED_UP", "IN_TRANSIT", "DELIVERED", "EXCEPTION", "CANCELLED",
];

const DIRECTION_LABELS: Record<string, string> = {
  INBOUND: "Inbound",
  OUTBOUND: "Outbound",
  DROP_SHIP: "Drop Ship",
  TRANSFER: "Transfer",
};

const MODE_LABELS: Record<string, string> = {
  OCEAN: "Ocean",
  AIR: "Air",
  RAIL: "Rail",
  FTL: "FTL",
  LTL: "LTL",
  PARCEL: "Parcel",
  OWN_FLEET: "Own Fleet",
  COURIER: "Courier",
  CUSTOMER_PICKUP: "Customer Pickup",
};

export function ShipmentDetailPage() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { sessionId } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: shipment, isLoading } = useQuery<ShipmentDetail>({
    queryKey: ["/api/shipments", params.id],
    queryFn: () => api.get<ShipmentDetail>(`/shipments/${params.id}`, sessionId),
    enabled: !!params.id,
  });

  const form = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: { status: "PLANNED" },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: EditFormData) =>
      api.patch(`/shipments/${params.id}`, data, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shipments"] });
      toast({ title: "Shipment updated", description: "Changes saved." });
      setEditOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update shipment",
        variant: "destructive",
      });
    },
  });

  const handleEditOpen = () => {
    if (shipment) {
      form.reset({
        status: shipment.status,
        trackingNumber: shipment.trackingNumber || "",
        proNumber: shipment.proNumber || "",
        containerNumber: shipment.containerNumber || "",
        billOfLading: shipment.billOfLading || "",
        notes: shipment.notes || "",
      });
    }
    setEditOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Shipment not found</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) =>
    d ? format(new Date(d), "MMM d, yyyy") : "—";
  const fmtMoney = (v: string | null) =>
    v == null ? "—" : `$${Number(v).toLocaleString()}`;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLocation("/shipments")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold tracking-tight">
              {shipment.shipmentNumber}
            </h1>
            <Badge variant="outline">
              {DIRECTION_LABELS[shipment.direction] || shipment.direction}
            </Badge>
            <ShipmentStatusBadge status={shipment.status} />
          </div>
          {shipment.mode && (
            <div className="text-sm text-muted-foreground">
              {MODE_LABELS[shipment.mode] || shipment.mode}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={handleEditOpen}>
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Truck className="h-4 w-4" />
                Shipping Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Carrier</p>
                <p className="text-sm font-medium">
                  {shipment.carrierName || shipment.carrier || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Tracking Number</p>
                <p className="text-sm font-medium">
                  {shipment.trackingNumber || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">PRO Number</p>
                <p className="text-sm font-medium">
                  {shipment.proNumber || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Container Number</p>
                <p className="text-sm font-medium">
                  {shipment.containerNumber || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bill of Lading</p>
                <p className="text-sm font-medium">
                  {shipment.billOfLading || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Booking Number</p>
                <p className="text-sm font-medium">
                  {shipment.bookingNumber || "—"}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4" />
                Origin & Destination
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Origin</p>
                {shipment.originWarehouse ? (
                  <p className="text-sm font-medium">
                    {shipment.originWarehouse.name}
                    <span className="text-muted-foreground ml-2">
                      ({shipment.originWarehouse.code})
                    </span>
                  </p>
                ) : shipment.vendor ? (
                  <p className="text-sm font-medium">{shipment.vendor.name}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Destination</p>
                {shipment.destinationWarehouse ? (
                  <p className="text-sm font-medium">
                    {shipment.destinationWarehouse.name}
                    <span className="text-muted-foreground ml-2">
                      ({shipment.destinationWarehouse.code})
                    </span>
                  </p>
                ) : shipment.order?.account?.name ? (
                  <p className="text-sm font-medium">
                    {shipment.order.account.name}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">—</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Calendar className="h-4 w-4" />
                Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Scheduled Pickup</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.scheduledPickup)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Actual Pickup</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.actualPickup)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Scheduled Arrival</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.scheduledArrival)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Actual Arrival</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.actualArrival)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ship Date</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.shipDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Estimated Arrival</p>
                <p className="text-sm font-medium">
                  {fmtDate(shipment.estimatedArrival)}
                </p>
              </div>
            </CardContent>
          </Card>

          {(shipment.podSignedBy || shipment.podSignedAt || shipment.podNotes) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="h-4 w-4" />
                  Proof of Delivery
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Signed By</p>
                  <p className="text-sm font-medium">
                    {shipment.podSignedBy || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Signed At</p>
                  <p className="text-sm font-medium">
                    {fmtDate(shipment.podSignedAt)}
                  </p>
                </div>
                {shipment.podNotes && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-muted-foreground">Notes</p>
                    <p className="text-sm">{shipment.podNotes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {shipment.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{shipment.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Linked Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {shipment.purchaseOrder ? (
                <button
                  onClick={() =>
                    setLocation(`/purchase-orders/${shipment.purchaseOrder?.id}`)
                  }
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline w-full text-left"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  PO {shipment.purchaseOrder.poNumber}
                </button>
              ) : null}
              {shipment.order ? (
                <button
                  onClick={() => setLocation(`/orders/${shipment.order?.id}`)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline w-full text-left"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  SO {shipment.order.orderNumber}
                </button>
              ) : null}
              {!shipment.purchaseOrder && !shipment.order && (
                <p className="text-sm text-muted-foreground">No linked documents</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4" />
                Costs
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Shipping Cost</p>
                <p className="text-sm font-medium">
                  {fmtMoney(shipment.shippingCost)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Cost</p>
                <p className="text-sm font-medium">
                  {fmtMoney(shipment.totalCost)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Package Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Weight</p>
                <p className="text-sm font-medium">
                  {shipment.weight
                    ? `${shipment.weight} ${shipment.weightUom || "LB"}`
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pieces</p>
                <p className="text-sm font-medium">
                  {shipment.pieces ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pallet Count</p>
                <p className="text-sm font-medium">
                  {shipment.palletCount ?? "—"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Shipment"
        description="Update shipment status and tracking info."
        formId="edit-shipment"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="md"
        data-testid="drawer-edit-shipment"
      >
        <Form {...form}>
          <form
            id="edit-shipment"
            onSubmit={form.handleSubmit((d) => updateMutation.mutate(d))}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="trackingNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tracking Number</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="proNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PRO #</FormLabel>
                    <FormControl>
                      <Input variant="flat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="containerNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Container #</FormLabel>
                    <FormControl>
                      <Input variant="flat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="billOfLading"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bill of Lading</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}

export default ShipmentDetailPage;
